import { useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { LocationState } from "../types";
import { isCacheValid as checkCacheValid } from "../utils";
import { useGetProcessUIData } from "../../../../hooks/entity";
import { useLmcUserView } from "../../../../contexts/AuthContext";

export const useProcessDataCache = (processId?: string) => {
  const location = useLocation();
  const { dispatch } = useLmcUserView();

  const navigationState = location.state as LocationState;
  const hasNavigationData = Boolean(navigationState?.processData);

  const isCacheValid = useMemo(() => {
    if (!hasNavigationData || !navigationState?.cacheTimestamp) {
      return false;
    }
    return checkCacheValid(navigationState.cacheTimestamp);
  }, [hasNavigationData, navigationState?.cacheTimestamp]);

  const cachedProcessData = isCacheValid
    ? navigationState?.processData || null
    : null;

  const hasDispatchedCache = useRef(false);

  const {
    data: processData,
    isLoading,
    isError,
    refetch,
  } = useGetProcessUIData(processId as string, {
    enabled: !isCacheValid,
    onSuccess: (data) => {
      dispatch({
        type: "ENTITY",
        payload: data,
      });
    },
  });

  const finalProcessData = cachedProcessData || processData;

  useEffect(() => {
    if (cachedProcessData && !hasDispatchedCache.current) {
      dispatch({ type: "ENTITY", payload: cachedProcessData });
      hasDispatchedCache.current = true;
    }
  }, [cachedProcessData, dispatch]);

  return {
    processData: finalProcessData,
    isLoading: isLoading && !isCacheValid,
    isError: isError && !isCacheValid,
    refetch,
  };
};
